import { ReactNode, useMemo } from 'react'

import { useInsightsContext } from '../../context/InsightsContext'
import { useFilters } from '../../hooks/useFilters'
import { useInsightsMetric } from '../../hooks/useInsightsMetric'
import {
  FilterValues,
  MetricResponsePayload,
  ScalarData,
} from '../../types/insights.types'
import {
  buildTrendTooltip,
  formatTrend,
  formatValueHero,
} from '../../utils/formatValue'
import { applyLabelOverrides } from '../../utils/metricView'
import { ChartSpec, MetricRenderer } from './MetricRenderer'

export type { ChartSpec } from './MetricRenderer'

export type MetricViewProps = {
  /** The backend metric slug that drives the chart body (and the hero if it's scalar). */
  metric: string
  /** Optional scalar metric slug for the headline value + trend above the chart. */
  heroMetric?: string
  /** Active filter values. Defaults to the domain's centralised `useFilters().values`. */
  filters?: FilterValues
  limit?: number
  /**
   * Override the card title (the metric payload's `title` otherwise). Pass a
   * pre-translated string, e.g. an app-localised heading for this metric.
   */
  title?: string
  /** Override the card subtitle (the metric payload's `subtitle` otherwise). */
  subtitle?: string
  /**
   * Explanatory text shown via an info icon + tooltip beside the title. Overrides
   * the metric payload's `description`; pass a pre-translated string.
   */
  description?: string
  /** Override breakdown series labels by series key (the rest keep their payload label). */
  seriesLabels?: Record<string, string>
  /** Override breakdown row labels by row key (the rest keep their payload label). */
  rowLabels?: Record<string, string>
  /**
   * Overrides the trend tooltip's comparison-window text. Defaults to the trend's
   * own date range (from the backend `period`); pass a pre-translated string to
   * describe the window differently (e.g. "Compared to the previous 30 days").
   * The prior-period value is appended either way.
   */
  trendTooltip?: string
  height?: number
  orientation?: 'vertical' | 'horizontal'
  wrapXAxisLabels?: boolean
  yAxisWidth?: number
  innerRadiusRatio?: number
  /** Line / area / combo chart: series keys plotted against the right Y-axis (dual-axis). */
  rightAxisSeries?: string[]
  /**
   * Pre-translated hover copy for a legend entry the chart couldn't plot — a
   * breakdown carrying three-plus units, where two axes can't scale them all.
   */
  notShownTooltip?: string
  showLegend?: boolean
  emptyState?: ReactNode
  errorLabel?: string
  heroErrorPlaceholder?: string
  testId?: string
}

type MetricViewInternalProps = MetricViewProps & { spec: ChartSpec }

const scalarOf = (
  payload: MetricResponsePayload | null
): ScalarData | undefined =>
  payload?.data.type === 'scalar' ? (payload.data as ScalarData) : undefined

/**
 * Shared shell behind the typed chart components: fetches the metric (+ an optional scalar
 * `heroMetric`), then hands the resolved data + StatCard frame (title/subtitle, hero value +
 * trend) and the fetch lifecycle to {@link MetricRenderer}. The chart body, legend, and
 * compatibility come from the supplied `spec`.
 */
export function MetricView({
  metric,
  heroMetric,
  spec,
  filters,
  limit,
  title: titleOverride,
  subtitle: subtitleOverride,
  description: descriptionOverride,
  seriesLabels,
  rowLabels,
  trendTooltip,
  height,
  orientation,
  wrapXAxisLabels,
  yAxisWidth,
  innerRadiusRatio,
  rightAxisSeries,
  notShownTooltip,
  showLegend = true,
  emptyState,
  errorLabel,
  heroErrorPlaceholder = '—',
  testId,
}: Readonly<MetricViewInternalProps>) {
  const { colourMap, seriesOrder, locale } = useInsightsContext()
  const { values: contextFilters } = useFilters()
  const activeFilters = filters ?? contextFilters
  const id = testId ?? metric

  const body = useInsightsMetric(metric, { filters: activeFilters, limit })
  const hero = useInsightsMetric(heroMetric ?? '', {
    filters: activeFilters,
    enabled: Boolean(heroMetric),
  })

  const heroScalar = heroMetric ? scalarOf(hero.data) : undefined
  const bodyScalar = scalarOf(body.data)
  const heroFailedCold =
    Boolean(heroMetric) && Boolean(hero.error) && !hero.data

  const label =
    titleOverride ?? (heroMetric ? hero.data?.title : body.data?.title) ?? ''

  let value: string | undefined

  if (heroMetric) {
    if (heroScalar) value = formatValueHero(heroScalar.value, locale)
    else if (heroFailedCold) value = heroErrorPlaceholder
  } else if (bodyScalar) {
    value = formatValueHero(bodyScalar.value, locale)
  }

  // The card subtitle is always the body metric's own subtitle (the hero metric,
  // when present, owns the label). No title fallback — an absent subtitle simply
  // shows nothing rather than echoing the metric title. Host overrides win.
  const subtitle = subtitleOverride ?? body.data?.subtitle
  // The description (info-icon tooltip beside the title) comes from the payload
  // unless the host overrides it; the body metric owns it even when a hero is set.
  const description = descriptionOverride ?? body.data?.description

  const trendData = formatTrend((heroScalar ?? bodyScalar)?.trend, locale)
  // The comparison-window text defaults to the trend's own date range (from the
  // backend `period`), so every card gets a tooltip with no per-card wiring. A
  // host can override it with its own localised copy (e.g. "Compared to the
  // previous 30 days") via `trendTooltip`; either way the lib appends the
  // prior-period value, e.g. "Jan 1 – 31, 2025 ($1,000)".
  const trend = trendData
    ? {
        value: trendData.value,
        direction: trendData.direction,
        tooltip: buildTrendTooltip(trendData, trendTooltip),
      }
    : undefined

  // Series/row label overrides are applied once here, so the legend and every
  // chart body (which all read labels straight off the data) re-label together.
  // `overrideSig` is the intentional proxy dep for `seriesLabels`/`rowLabels`: a
  // value-stable string signature, so an inline override object (new ref each
  // render) doesn't bust the memo and re-fire the bodies' animation memos. The
  // exhaustive-deps disable below is therefore safe — the closure vars ARE
  // covered, via the signature. Don't add them to the array directly or inline
  // objects would recompute every render. With no overrides the signature is ''
  // and `applyLabelOverrides` returns the original (already stable) data ref, so
  // we also skip the stringify entirely.
  const overrideSig =
    seriesLabels || rowLabels ? JSON.stringify({ seriesLabels, rowLabels }) : ''
  const renderData = useMemo(
    () =>
      body.data
        ? applyLabelOverrides(body.data.data, { seriesLabels, rowLabels })
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seriesLabels/rowLabels covered by overrideSig
    [body.data, overrideSig]
  )

  const isShowingPlaceholder =
    body.isShowingPlaceholder ||
    (Boolean(heroMetric) && hero.isShowingPlaceholder)

  return (
    <MetricRenderer
      data={renderData}
      spec={spec}
      colourMap={colourMap}
      seriesOrder={seriesOrder}
      label={label}
      value={value}
      subtitle={subtitle}
      description={description}
      trend={trend}
      showLegend={showLegend}
      error={body.error}
      isLoading={body.isLoading}
      isShowingPlaceholder={isShowingPlaceholder}
      height={height}
      locale={locale}
      orientation={orientation}
      wrapXAxisLabels={wrapXAxisLabels}
      yAxisWidth={yAxisWidth}
      innerRadiusRatio={innerRadiusRatio}
      rightAxisSeries={rightAxisSeries}
      notShownTooltip={notShownTooltip}
      emptyState={emptyState}
      errorLabel={errorLabel}
      testId={id}
    />
  )
}
